import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Flex } from '~/shared/components';
import useDynamicRecoil from '~/shared/hooks/useDynamicRecoil';
import { DateFilterResolutionOption } from '../types';
import DateFilterResolutionButton from './DateFilterResolutionButton';

const rangeButtonCss = css`
  color: #33485e;
  font-size: 14px;
  font-weight: 400;
  border: 1px solid #33485e;
  border-radius: 30px;
  background: transparent;
  width: 40px;
  padding: 7px 15px 6px;
  margin: 16px 12px;
  text-transform: unset !important;
`;

const rangeButtonActiveCss = css`
  color: white !important;
  font-size: 14px;
  font-weight: 400;
  border: 1px solid var(--filter-bar-range-button-active-border) !important;
  border-radius: 30px;
  background: var(--filter-bar-range-button-active-background) !important;
  width: 40px;
  max-width: 40px;
  padding: 7px 15px 6px;
  margin: 16px 12px;
  text-transform: unset !important;
`;

const Wrapper = styled(Flex)`
  width: 100%;
  justify-content: flex-start;
  .range-button {
    ${rangeButtonCss};
    &:hover {
      ${rangeButtonActiveCss};
    }
    &:active {
      ${rangeButtonActiveCss};
    }
  }
  .range-button-selected {
    ${rangeButtonActiveCss};
    &:hover {
      ${rangeButtonActiveCss};
    }
    :active {
      ${rangeButtonActiveCss};
    }
  }
`;

const DEFAULT_RECOIL_KEY = 'dateFilterResolutionButton';
const DEFAULT_FALLBACK_RESOLUTION = '7-Day';

export interface DateFilterResolutionButtonsProps {
  options?: DateFilterResolutionOption[];
  filters?: any;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  className?: string;
}

const deriveLabelFromId = (resolutionId: string) => {
  if (!resolutionId) return '';
  const sanitized = resolutionId.replace('Trending ', '').replace(' ', '-');
  const [amount, unit] = sanitized.split('-');
  if (!amount) return resolutionId;
  const unitInitial = unit?.[0]?.toUpperCase() ?? '';
  return `${amount}${unitInitial}`;
};

const resolveOptionsFromFilters = (filters?: any): DateFilterResolutionOption[] | undefined => {
  if (!filters) return undefined;
  const formation = filters.find((f: any) => f.id === 'Formation Period');
  if (formation?.options) return formation.options;
  const timeResolution = filters.find((f: any) => f.id === 'Time Resolution');
  return timeResolution?.options;
};

const resolveDefaultFromFilters = (filters?: any): string | undefined => {
  if (!filters) return undefined;
  const formation = filters.find((f: any) => f.id === 'Formation Period');
  if (formation?.defaultValue) return formation.defaultValue;
  const timeResolution = filters.find((f: any) => f.id === 'Time Resolution');
  return timeResolution?.defaultValue;
};

const DateFilterResolutionButtons = ({
  options,
  filters,
  value,
  defaultValue,
  onChange,
  className,
}: DateFilterResolutionButtonsProps) => {
  const location = useLocation();
  const search = location?.search;
  const urlResolution = useMemo(() => {
    const resolutionQuery = search?.split('&')[1]?.split('=')[1];
    return resolutionQuery?.replace(/\+/g, ' ');
  }, [search]);

  const resolvedOptions = useMemo(() => {
    if (options && options.length) {
      return options;
    }
    const derived = resolveOptionsFromFilters(filters);
    if (Array.isArray(derived) && derived.length) {
      return derived;
    }
    return [] as DateFilterResolutionOption[];
  }, [options, filters]);

  const fallbackValue = resolvedOptions[0]?.id || DEFAULT_FALLBACK_RESOLUTION;
  const filtersDefault = useMemo(() => resolveDefaultFromFilters(filters), [filters]);
  const resolvedDefaultValue = defaultValue ?? filtersDefault ?? fallbackValue;

  const initialValue = urlResolution || resolvedDefaultValue || fallbackValue;

  const [localValue, setLocalValue] = useDynamicRecoil(DEFAULT_RECOIL_KEY, initialValue);

  if (!resolvedOptions.length) {
    return <div />;
  }

  const isControlled = value !== undefined;
  const activeValue = isControlled ? value : localValue;

  useEffect(() => {
    if (isControlled) return;
    setLocalValue(urlResolution || resolvedDefaultValue || fallbackValue);
  }, [fallbackValue, isControlled, resolvedDefaultValue, setLocalValue, urlResolution]);

  const handleSelect = useCallback(
    (nextValue: string) => {
      if (!isControlled) {
        setLocalValue(nextValue);
      }
      onChange?.(nextValue);
    },
    [isControlled, onChange, setLocalValue],
  );

  return (
    <Wrapper className={className}>
      {resolvedOptions.map(option => {
        const optionId = option.id;
        const label = option.label ?? deriveLabelFromId(optionId);
        return (
          <DateFilterResolutionButton
            key={optionId}
            text={label}
            value={optionId}
            selected={optionId === activeValue}
            onSelect={handleSelect}
          />
        );
      })}
    </Wrapper>
  );
};

export default DateFilterResolutionButtons;
